import { GET, PUT } from "config/api.config";
import { USER_SETTING } from "../config/apiEndPoint";
import {
  applyTableColumnPreferenceUserSettingToStorage,
  applyTablePageSizeUserSettingToStorage,
  applyCallDetailsTabOrderUserSettingToStorage,
} from "helpers/GeneralHelper";
import { applyFilterPreferenceUserSettingToStorage } from "helpers/metadataFilterHelpers";
import {
  FONT_SCALE_STORAGE_KEY,
  FONT_FAMILY_STORAGE_KEY,
  FONT_COLOR_STORAGE_KEY,
  UI_THEME_STORAGE_KEY,
  fontScaleTypes,
  fontFamilyTypes,
  fontColorTypes,
  uiThemeTypes,
} from "constants/layout";

const isAuthenticated = () => {
  try {
    return Boolean(JSON.parse(localStorage.getItem("authUser")));
  } catch {
    return false;
  }
};

const normalizeTableColumnPreferencesValue = (settingsValue = {}) => ({
  columnOrder: Array.isArray(settingsValue?.columnOrder)
    ? settingsValue.columnOrder
    : [],
  visibleColumns:
    settingsValue?.visibleColumns && typeof settingsValue.visibleColumns === "object"
      ? settingsValue.visibleColumns
      : {},
});

const normalizeTablePageSizeSettingsValue = (settingsValue) => {
  const pageSize = Number(
    typeof settingsValue === "object" && settingsValue !== null
      ? settingsValue.pageSize
      : settingsValue
  );
  return Number.isFinite(pageSize) ? pageSize : null;
};

const normalizeHeaderSettingValue = (settingsKey = "", settingsValue = null) => {
  const key = String(settingsKey ?? "").trim();
  let parsed = settingsValue;

  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      parsed = settingsValue;
    }
  }

  const rawValue =
    parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? parsed.value
      : parsed;

  if (key === FONT_SCALE_STORAGE_KEY) {
    return rawValue === fontScaleTypes.SMALL ||
      rawValue === fontScaleTypes.CURRENT ||
      rawValue === fontScaleTypes.LARGE
      ? rawValue
      : null;
  }

  if (key === FONT_FAMILY_STORAGE_KEY) {
    return rawValue === fontFamilyTypes.POPPINS ||
      rawValue === fontFamilyTypes.ROBOTO ||
      rawValue === fontFamilyTypes.OPEN_SANS
      ? rawValue
      : null;
  }

  if (key === FONT_COLOR_STORAGE_KEY) {
    return rawValue === fontColorTypes.CURRENT ||
      rawValue === fontColorTypes.CHARCOAL ||
      rawValue === fontColorTypes.SOFT_NAVY ||
      rawValue === fontColorTypes.RED ||
      rawValue === fontColorTypes.PRIMARY ||
      rawValue === fontColorTypes.DEEP_SAPPHIRE ||
      rawValue === fontColorTypes.DEEP_MAROON ||
      rawValue === fontColorTypes.DEEP_BLACK
      ? rawValue
      : null;
  }

  if (key === UI_THEME_STORAGE_KEY) {
    return rawValue === uiThemeTypes.CURRENT ||
      rawValue === uiThemeTypes.MODERN ||
      rawValue === uiThemeTypes.DARK
      ? rawValue
      : null;
  }

  return null;
};

/** PUT `user/UserSetting` — persist a single user setting. */
export const saveUserSetting = async ({ settingsKey = "", settingsValue = null } = {}) => {
  const key = String(settingsKey ?? "").trim();
  if (!key || !isAuthenticated()) return null;

  const serializedSettingsValue =
    typeof settingsValue === "string"
      ? settingsValue
      : JSON.stringify(settingsValue ?? {});

  try {
    const response = await PUT(USER_SETTING.UPDATE, {
      settingsKey: key,
      settingsValue: serializedSettingsValue,
    });
    return response;
  } catch {
    return null;
  }
};

/** PUT table column prefs using the same key as localStorage (`ats_table_column_prefs_*`). */
export const saveTableColumnPreferencesUserSetting = async (
  settingsKey = "",
  preferences = {}
) =>
  saveUserSetting({
    settingsKey,
    settingsValue: normalizeTableColumnPreferencesValue(preferences),
  });

const normalizeFilterPreferencesSettingsValue = (settingsValue) => {
  let parsed = settingsValue;

  if (typeof settingsValue === "string") {
    try {
      parsed = JSON.parse(settingsValue);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== "object") return null;

  return {
    toolbarFieldOrder: Array.isArray(parsed.toolbarFieldOrder)
      ? parsed.toolbarFieldOrder
      : [],
    advancedFieldOrder: Array.isArray(parsed.advancedFieldOrder)
      ? parsed.advancedFieldOrder
      : [],
  };
};

/** PUT table page size using the same key as localStorage (`ats_table_page_size_*`). */
export const saveTablePageSizeUserSetting = async (settingsKey = "", pageSize) => {
  const normalizedPageSize = normalizeTablePageSizeSettingsValue(pageSize);
  if (!Number.isFinite(normalizedPageSize)) return null;

  return saveUserSetting({
    settingsKey,
    settingsValue: { pageSize: normalizedPageSize },
  });
};

/** PUT filter toolbar prefs using the same key as localStorage (`ats_filter_prefs_*`). */
export const saveFilterPreferencesUserSetting = async (settingsKey = "", preferences = {}) =>
  saveUserSetting({
    settingsKey,
    settingsValue: normalizeFilterPreferencesSettingsValue(preferences),
  });

export const saveHeaderSettingUserSetting = async (settingsKey = "", value = "") => {
  const normalizedValue = normalizeHeaderSettingValue(settingsKey, value);
  if (!normalizedValue) return null;

  return saveUserSetting({
    settingsKey,
    settingsValue: { value: normalizedValue },
  });
};

/** GET `user/UserSetting` — list all user settings. */
export const fetchAllUserSettings = async () => {
  if (!isAuthenticated()) return null;

  try {
    const response = await GET(USER_SETTING.LIST);
    return response;
  } catch {
    return null;
  }
};

/** GET `user/UserSetting?settingsKey=` — load one user setting. */
export const fetchUserSetting = async (settingsKey = "") => {
  const key = String(settingsKey ?? "").trim();
  if (!key || !isAuthenticated()) return null;

  try {
    const response = await GET(USER_SETTING.BY_KEY(key));
    return response;
  } catch {
    return null;
  }
};

const emptySyncResult = () => ({
  columnKeys: [],
  pageSizeKeys: [],
  filterKeys: [],
  tabOrderKeys: [],
  headerSettings: null,
  updatedKeys: [],
});

/** Shared in-flight promise so parallel callers reuse one GET. */
let inFlightSyncAllUserSettingsPromise = null;

/** Apply one GET `user/UserSetting` payload into localStorage for every preference type. */
export const applyAllUserSettingsItems = (items = []) => {
  const list = Array.isArray(items) ? items : [];
  const columnKeys = [];
  const pageSizeKeys = [];
  const filterKeys = [];
  const tabOrderKeys = [];
  const nextHeaderSettings = {};

  list.forEach((item) => {
    const settingsKey = String(item?.settingsKey ?? "").trim();
    if (!settingsKey) return;

    if (
      applyTableColumnPreferenceUserSettingToStorage(
        settingsKey,
        item?.settingsValue
      )
    ) {
      columnKeys.push(settingsKey);
    }

    if (
      applyTablePageSizeUserSettingToStorage(settingsKey, item?.settingsValue)
    ) {
      pageSizeKeys.push(settingsKey);
    }

    if (
      applyFilterPreferenceUserSettingToStorage(settingsKey, item?.settingsValue)
    ) {
      filterKeys.push(settingsKey);
    }

    if (
      applyCallDetailsTabOrderUserSettingToStorage(
        settingsKey,
        item?.settingsValue
      )
    ) {
      tabOrderKeys.push(settingsKey);
    }

    const normalizedHeaderValue = normalizeHeaderSettingValue(
      settingsKey,
      item?.settingsValue
    );
    if (!normalizedHeaderValue) return;

    localStorage.setItem(settingsKey, normalizedHeaderValue);
    if (settingsKey === FONT_SCALE_STORAGE_KEY) {
      nextHeaderSettings.fontScale = normalizedHeaderValue;
    } else if (settingsKey === FONT_FAMILY_STORAGE_KEY) {
      nextHeaderSettings.fontFamily = normalizedHeaderValue;
    } else if (settingsKey === FONT_COLOR_STORAGE_KEY) {
      nextHeaderSettings.fontColor = normalizedHeaderValue;
    } else if (settingsKey === UI_THEME_STORAGE_KEY) {
      nextHeaderSettings.uiTheme = normalizedHeaderValue;
    }
  });

  const headerSettings =
    Object.keys(nextHeaderSettings).length > 0 ? nextHeaderSettings : null;

  return {
    columnKeys,
    pageSizeKeys,
    filterKeys,
    tabOrderKeys,
    headerSettings,
    updatedKeys: [
      ...columnKeys,
      ...pageSizeKeys,
      ...filterKeys,
      ...tabOrderKeys,
      ...(headerSettings ? Object.keys(headerSettings) : []),
    ],
  };
};

/**
 * Single GET `user/UserSetting` for app bootstrap / login.
 * Hydrates columns, page size, filters, tab order, and header settings.
 */
export const syncAllUserSettingsFromServer = async () => {
  if (!isAuthenticated()) return emptySyncResult();

  if (inFlightSyncAllUserSettingsPromise) {
    return inFlightSyncAllUserSettingsPromise;
  }

  inFlightSyncAllUserSettingsPromise = (async () => {
    try {
      const response = await fetchAllUserSettings();
      if (!response?.success) return emptySyncResult();

      return applyAllUserSettingsItems(
        Array.isArray(response?.data) ? response.data : []
      );
    } finally {
      inFlightSyncAllUserSettingsPromise = null;
    }
  })();

  return inFlightSyncAllUserSettingsPromise;
};

/** GET all settings and hydrate table column prefs in localStorage. */
export const syncTableColumnPreferencesFromUserSettings = async () => {
  const result = await syncAllUserSettingsFromServer();
  return result.columnKeys;
};

/** GET all settings and hydrate table page size prefs in localStorage. */
export const syncTablePageSizePreferencesFromUserSettings = async () => {
  const result = await syncAllUserSettingsFromServer();
  return result.pageSizeKeys;
};

/** GET all settings and hydrate filter toolbar prefs in localStorage. */
export const syncFilterPreferencesFromUserSettings = async () => {
  const result = await syncAllUserSettingsFromServer();
  return result.filterKeys;
};

/** GET all settings and hydrate call-details tab order in localStorage. */
export const syncCallDetailsTabOrderFromUserSettings = async () => {
  const result = await syncAllUserSettingsFromServer();
  return result.tabOrderKeys;
};

/** GET all settings and hydrate header/UI prefs in localStorage. */
export const syncHeaderSettingsFromUserSettings = async () => {
  const result = await syncAllUserSettingsFromServer();
  return result.headerSettings;
};

export const extractUserSettingValueFromResponse = (response) => {
  const data = response?.data ?? response;
  if (data == null) return null;

  const rawValue =
    typeof data === "object" && data !== null && "settingsValue" in data
      ? data.settingsValue
      : data;

  if (typeof rawValue === "string") {
    try {
      return JSON.parse(rawValue);
    } catch {
      return rawValue;
    }
  }

  return rawValue;
};
